import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

import { DialogFullscreenState } from '@shared/dialogs/full-screen-dialog.base';

export type DialogHeaderFullScreenMode = 'default' | 'always' | 'never';

@Component({
  selector: 'cipo-dialog-header',
  host: {
    '[attr.data-testid]': 'testId()',
  },
  hostDirectives: [MatDialogTitle],
  imports: [MatButtonModule, MatIconModule, TranslateModule],
  template: `
    <div class="cipo-dialog-header__title">
      <ng-content>
        @let translatedTitle = title() | translate: titleParams();
        <h5 class="m-0 cipo--text--ellipsed" [title]="translatedTitle">{{ translatedTitle }}</h5>
      </ng-content>
    </div>
    <div class="cipo-dialog-header__actions">
      @if (showFullscreen()) {
        <button
          mat-icon-button
          type="button"
          (click)="fullscreen.toggleFullscreen()"
          [attr.aria-label]="
            (fullscreen.isFullscreen() ? 'a11y.dialog.fullscreenExit' : 'a11y.dialog.fullscreenEnter') | translate
          ">
          <mat-icon class="material-symbols-outlined" aria-hidden="true">
            {{ fullscreen.isFullscreen() ? 'close_fullscreen' : 'fullscreen' }}
          </mat-icon>
        </button>
      }
      @if (showClose()) {
        <button
          mat-icon-button
          type="button"
          [attr.data-testid]="closeTestId()"
          [attr.aria-label]="'a11y.dialog.close' | translate"
          (click)="onCloseClick()">
          <mat-icon aria-hidden="true">close</mat-icon>
        </button>
      }
    </div>
  `,
  styles: `
    :host {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
      min-width: 0;
    }

    .cipo-dialog-header__title {
      flex: 1 1 auto;
      display: flex;
      align-items: center;
      min-width: 0;
      overflow: hidden;
    }

    .cipo-dialog-header__actions {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      gap: 8px;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CipoDialogHeaderComponent {
  readonly title = input<string>('');
  readonly titleParams = input<Record<string, unknown>>({});
  readonly fullScreen = input<DialogHeaderFullScreenMode>('default');
  readonly testId = input<string | undefined>(undefined);
  readonly closeTestId = input<string | undefined>(undefined);
  readonly closeResult = input<unknown>(undefined);
  readonly customCloseMode = input(false);
  readonly hideCloseWhenDisabled = input(false);
  readonly hideClose = input(false);

  readonly closeClick = output<void>();

  private readonly dialogRef = inject(MatDialogRef<unknown>);
  readonly fullscreen = new DialogFullscreenState(this.dialogRef);

  readonly showFullscreen = computed(() => {
    const mode = this.fullScreen();
    if (mode === 'never') {
      return false;
    }
    if (mode === 'always') {
      return true;
    }
    return this.fullscreen.canToggleFullscreen();
  });

  readonly showClose = computed(
    () => !this.hideClose() && !(this.hideCloseWhenDisabled() && this.dialogRef.disableClose),
  );

  onCloseClick(): void {
    if (this.customCloseMode()) {
      this.closeClick.emit();
      return;
    }

    this.dialogRef.close(this.closeResult());
  }
}
